import { create } from 'zustand';
import { fetchRuntimeConfig } from '../api/client';

// Module-level memoization: the runtime config is fetched exactly once per page
// load. Protects against React StrictMode double-invocation of App's effect and
// keeps every consumer reading one answer instead of racing its own request.
let loadPromise: Promise<void> | null = null;

interface ConfigState {
  /** Is the Shopify connector offered in the connect grid?
   *
   *  🔴 Fail-closed: false while the request is in flight, false on a network
   *  error, a non-200 or a body that doesn't carry a literal `true`. The flag
   *  exists to HIDE an integration that is under review, so a config call that
   *  never answered must hide it, never reveal it (VIBE-1786). */
  shopifyIntegrationEnabled: boolean;
  /** True once a response has been applied. Diagnostics only — consumers must
   *  not wait on it: the fail-closed default is already the right answer. */
  loaded: boolean;
  loadRuntimeConfig: () => Promise<void>;
}

export const useConfigStore = create<ConfigState>((set) => ({
  shopifyIntegrationEnabled: false,
  loaded: false,

  loadRuntimeConfig: () => {
    if (loadPromise) return loadPromise;

    loadPromise = fetchRuntimeConfig()
      .then((config) => {
        // `=== true` and not a truthiness test: a body that lost the field, or
        // carries the string "false", must not read as enabled.
        set({
          shopifyIntegrationEnabled: config.shopify_integration_enabled === true,
          loaded: true,
        });
      })
      .catch(() => {
        // Network error, non-200, non-JSON body — stay closed and leave
        // `loaded` false, so the state reads as "never answered" rather than
        // "answered no".
      });

    return loadPromise;
  },
}));
